import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Document, DocumentUploadResult } from '../models/document';
import { Observable, Subject, from, concatMap, delay, tap, of, map } from 'rxjs';
import {
  AiDecisionRequest,
  AiJobResponse,
  AiValidationResult,
  AiDocumentType,
  AiJobStatus,
  AiComparisonStatus,
} from '../models/ai-document-processing';

const MOCK_CIN_AI_VALIDATION_RESULT: AiValidationResult = {
  documentId: 1,
  documentType: AiDocumentType.CIN_CARD,
  status: AiJobStatus.SUCCESS,
  fields: [
    {
      field: 'cin_number',
      // i18n-ignore
      label: 'CIN Number',
      manualValue: 'AB123456',
      aiValue: 'AB123456',
      confidence: 0.99,
      comparisonStatus: AiComparisonStatus.MATCH,
    },
    {
      field: 'address',
      // i18n-ignore
      label: 'Address',
      manualValue: '123 Main St',
      aiValue: '123 Main Street',
      confidence: 0.95,
      comparisonStatus: AiComparisonStatus.MISMATCH,
      warning: 'Address slightly differs from manual entry', // i18n-ignore
    },
  ],
  warnings: [],
};



const MOCK_CNSS_AI_VALIDATION_RESULT: AiValidationResult = {
  documentId: 2,
  documentType: AiDocumentType.CNSS_CARD,
  status: AiJobStatus.SUCCESS,
  fields: [
    {
      field: 'cnss_number',
      // i18n-ignore
      label: 'CNSS Number',
      manualValue: '987654321',
      aiValue: '987654321',
      confidence: 0.98,
      comparisonStatus: AiComparisonStatus.MATCH,
    },
  ],
  warnings: [],
};

const MOCK_BIRTH_CERTIFICATE_AI_VALIDATION_RESULT: AiValidationResult = {
  documentId: 3,
  documentType: AiDocumentType.BIRTH_CERTIFICATE,
  status: AiJobStatus.SUCCESS,
  fields: [
    {
      field: 'child_full_name',
      // i18n-ignore
      label: 'Child Full Name',
      manualValue: 'John Doe',
      aiValue: 'JOHN DOE',
      confidence: 0.97,
      comparisonStatus: AiComparisonStatus.MATCH,
    },
    {
      field: 'date_of_birth',
      // i18n-ignore
      label: 'Date of Birth',
      manualValue: '2020-01-01',
      aiValue: '2020-01-01',
      confidence: 0.99,
      comparisonStatus: AiComparisonStatus.MATCH,
    },
  ],
  warnings: [],
};

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  API_URL = `${environment.apiUrl}/documents`;
  private readonly useAiMocks = environment.useAiDocumentMocks ?? false;
  private uploadProgressSubject = new Subject<{ progress: number; message: string }>();

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
  ) { }

  public deleteDocument(documentId?: number): Observable<any> {
    return this.http.delete<any>(`${this.API_URL}/${documentId}`);
  }

  public uploadFiles(id: number, docType: string, files: File[]): Observable<Document[]> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    return this.http.post<Document[]>(`${this.API_URL}/upload-files/employee-id/${id}/doc-type/${docType}`, formData);
  }

  public uploadSalaryAdvanceFile(employeeId: number, file: File): Observable<Document[]> {
    return this.uploadFiles(employeeId, 'SALARY_ADVANCE', [file]);
  }

  public updateEmployeeDocument(
    employeeId: number,
    documentId: number,
    docType: string,
    documentDate: Date,
    file: File
  ): Observable<Document> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('docType', docType);
    formData.append('documentDate', documentDate.toISOString());

    return this.http.put<Document>(
      `${this.API_URL}/employee-id/${employeeId}/document-id/${documentId}`,
      formData
    );
  }

  public uploadFilesByDocType(
    docType: string,
    files: File[],
    period?: { month: number; year: number }
  ): Observable<DocumentUploadResult> {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append('files', file);
    });

    if (period) {
      formData.append('month', String(period.month));
      formData.append('year', String(period.year));
    }

    return this.http.post<DocumentUploadResult>(`${this.API_URL}/upload-files/doc-type/${docType}`, formData);
  }

  public checkPayslipEmployeeNumbers(employeeNumbers: string[]): Observable<string[]> {
    return this.http
      .post<{ unknownEmployeeNumbers: string[] }>(`${this.API_URL}/payroll/check-employee-numbers`, employeeNumbers)
      .pipe(map((res) => res?.unknownEmployeeNumbers ?? []));
  }

  public uploadFilesByDocTypeInBatches(
    docType: string,
    files: File[],
    period?: { month: number; year: number }
  ): Observable<DocumentUploadResult[]> {
    const batchSize = this.calculateOptimalBatchSize(files.length);

    return new Observable(observer => {
      const batches = this.createBatches(files, batchSize);
      const results: DocumentUploadResult[] = [];
      let completedBatches = 0;

      from(batches).pipe(
        concatMap((batch, index) => {
          const batchNumber = index + 1;
          const totalBatches = batches.length;

          this.uploadProgressSubject.next({
            progress: (completedBatches / totalBatches) * 100,
            message: this.translate.instant('shared.documents.processingBatch', {
              batchNumber,
              totalBatches,
              count: batch.length,
            }),
          });

          return this.uploadFilesByDocType(docType, batch, period).pipe(
            tap(() => completedBatches++),
            delay(1000)
          );
        })
      ).subscribe({
        next: (result) => results.push(result),
        error: (error) => observer.error(error),
        complete: () => {
          observer.next(results);
          observer.complete();
        }
      });
    });
  }

  private calculateOptimalBatchSize(totalFiles: number): number {
    if (totalFiles <= 10) return 5;
    if (totalFiles <= 50) return 10;
    if (totalFiles <= 200) return 25;
    if (totalFiles <= 500) return 50;
    return 20;
  }

  private createBatches(files: File[], batchSize: number): File[][] {
    const batches: File[][] = [];
    for (let i = 0; i < files.length; i += batchSize) {
      batches.push(files.slice(i, i + batchSize));
    }
    return batches;
  }

  public getUploadProgress(): Observable<{ progress: number, message: string }> {
    return this.uploadProgressSubject.asObservable();
  }

  public getDoumentsByEmployeeId(employeeId: number): Observable<Document[]> {
    return this.http.get<Document[]>(`${this.API_URL}/employee-id/${employeeId}`);
  }

  public getDoumentsByEmployeeIdAndDocType(employeeId: number, docType: string): Observable<Document[]> {
    return this.http.get<Document[]>(`${this.API_URL}/employee-id/${employeeId}/doc-type/${docType}`);
  }

  public startAiExtraction(documentId: number): Observable<AiJobResponse> {
    if (this.useAiMocks) {
      return of({ jobId: 'mock-job-id', status: 'PENDING' }).pipe(delay(500));
    }
    return this.http.post<AiJobResponse>(`${this.API_URL}/${documentId}/ai-extraction/start`, {});
  }

  public getAiExtractionStatus(documentId: number): Observable<AiValidationResult> {
    if (this.useAiMocks) {
      let mockResult = MOCK_CIN_AI_VALIDATION_RESULT;
      if (documentId % 3 === 0) {
        mockResult = MOCK_BIRTH_CERTIFICATE_AI_VALIDATION_RESULT;
      } else if (documentId % 2 === 0) {
        mockResult = MOCK_CNSS_AI_VALIDATION_RESULT;
      }

      return of({ ...mockResult, documentId }).pipe(delay(500));
    }
    return this.http.get<AiValidationResult>(`${this.API_URL}/${documentId}/ai-extraction/status`);
  }

  public submitAiExtractionDecision(documentId: number, decision: AiDecisionRequest): Observable<void> {
    if (this.useAiMocks) {
      return of(undefined).pipe(delay(500));
    }
    return this.http.post<void>(`${this.API_URL}/${documentId}/ai-extraction/decision`, decision);
  }

  // TODO: Implement this method once the backend /retry endpoint is available.
  /*
  public retryAiExtraction(documentId: number): Observable<AiJobResponse> {
    if (this.useAiMocks) {
      return of({ jobId: 'mock-job-id-retry', status: 'PENDING' }).pipe(delay(500));
    }
    return this.http.post<AiJobResponse>(`${this.API_URL}/${documentId}/ai-extraction/retry`, {});
  }
  */

  public downloadSelectedFilesInZipFormat(
    employeeNumber: string,
    zipFilePrefix: string,
    fileNames: string[],
  ): Observable<Blob> {
    return this.http.post(`${this.API_URL}/download/documents/${zipFilePrefix}/${employeeNumber}`, fileNames, {
      responseType: 'blob',
    });
  }

  getAllDocumentsTemplate(): Observable<Document[]> {
    return this.http.get<Document[]>(`${this.API_URL}/templates`);
  }

  uploadReferenceFile(document: Document, file: File): Observable<Document> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', document.description || '');
    return this.http.post<Document>(`${this.API_URL}/upload-reference`, formData);
  }

  updateReferenceFile(document: Document, file: File): Observable<Document> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', document.description || '');
    return this.http.put<Document>(`${this.API_URL}/update-reference/${document.id}`, formData);
  }
  public downloadDocument(documentId: number): Observable<Blob> {
    return this.http.get(`${this.API_URL}/download/document-id/${documentId}`, {
      responseType: 'blob'
    });
  }

  public downloadSalaryAdvanceDocument(advanceId: number): Observable<Blob> {
    return this.http.get(`${environment.apiUrl}/salary-advances/${advanceId}/document`, {
      responseType: 'blob'
    });
  }
  public downloadSalaryTemplate(): Observable<Blob> {
    return this.http.get(`${this.API_URL}/templates/salary`, {
      responseType: 'blob'
    });
  }

  public uploadSalaryFile(formData: FormData): Observable<any> {
    return this.http.post(`${this.API_URL}/salary/import`, formData);
  }

  uploadDocumentForFeedback(
    file: File,
    interviewType: string,
    promotionId: number,
    docType: string
  ): Observable<Document> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('interviewType', interviewType);
    formData.append('promotionId', promotionId.toString());
    formData.append('docType', docType);

    return this.http.post<Document>(`${this.API_URL}/upload`, formData);
  }

  public uploadDocumentsForClient(clientId: number, files: File[]): Observable<Document[]> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    return this.http.post<Document[]>(`${this.API_URL}/upload-files/client-id/${clientId}`, formData);
  }

  public getDocumentsByClientId(clientId: number): Observable<Document[]> {
    return this.http.get<Document[]>(`${this.API_URL}/client-id/${clientId}`);
  }

  public updateDocument(
    employeeId: number,
    documentId: number,
    docType: string,
    documentDate: Date,
    file?: File
): Observable<Document> {
    const formData = new FormData();
    formData.append('docType', docType);
    formData.append('documentDate', documentDate.toISOString());
    if (file) {
        formData.append('file', file);
    }

    return this.http.put<Document>(
        `${this.API_URL}/employee-id/${employeeId}/document-id/${documentId}`,
        formData
    );
}
}
